/**
 * BrowserSidebar — left sidebar for the file browser.
 *
 * Sections:
 *   - Recent workspaces (optional direct-open shortcuts)
 *   - Favorites (user-pinned folders, supports drag-to-reorder)
 *   - Recents (recently visited directories)
 *   - Allowed roots
 *   - Go-to path input at bottom
 */

import {
    ChevronDown,
    ChevronRight,
    Clock,
    Folder,
    HardDrive,
    Heart,
    Home,
    Star,
    Trash2,
    X,
} from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { workspaceFs } from "../../lib/engine-fs";
import { isPathWithinRoots, normalizeUserPath } from "../../lib/path-utils";
import { cn } from "../../lib/utils";
import { getNotificationBus } from "../../store/notification-bus";
import { useRecentStore, type RecentItem } from "../../store/recent-store";
import {
    addFavorite,
    clearRecents,
    DRAG_MIME_PATH,
    type FileBrowserBookmark,
    getFavorites,
    getRecents,
    removeFavorite,
    removeRecent,
    reorderFavorites,
} from "./file-browser-store";
import type { WorkspaceAllowedRoot } from "@magic-md/engine-client/protocol";

export interface BrowserSidebarProps {
    /** Called when user clicks a sidebar item to navigate */
    onNavigate: (path: string) => void;
    /** Optional direct-open handler for recent workspace/folder shortcuts. */
    onOpenRecentWorkspace?: (item: RecentItem) => void | Promise<void>;
    /** Current active path (for highlighting) */
    currentPath: string;
    /** Path separator */
    sep: string;
}

/* ── Component ── */

export function BrowserSidebar({
    onNavigate,
    onOpenRecentWorkspace,
    currentPath,
    sep,
}: BrowserSidebarProps) {
    const t = useT("editor");
    const [allowedRoots, setAllowedRoots] = useState<WorkspaceAllowedRoot[]>([]);
    const [recents, setRecents] = useState<FileBrowserBookmark[]>([]);
    const [favorites, setFavorites] = useState<FileBrowserBookmark[]>([]);
    const [pathInput, setPathInput] = useState("");
    const [pathInputVisible, setPathInputVisible] = useState(false);
    const [pathError, setPathError] = useState<string | null>(null);
    const pathInputRef = useRef<HTMLInputElement>(null);

    // Drag state for favorites reorder
    const [dragIndex, setDragIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [externalDragOver, setExternalDragOver] = useState(false);

    // Collapse state for sections. The top recent-workspace shortcuts stay open;
    // utility sections follow the main sidebar convention and start collapsed.
    const [recentWorkspacesCollapsed, setRecentWorkspacesCollapsed] = useState(false);
    const [favoritesCollapsed, setFavoritesCollapsed] = useState(true);
    const [recentsCollapsed, setRecentsCollapsed] = useState(true);
    const [allowedRootsCollapsed, setAllowedRootsCollapsed] = useState(true);

    const recentItems = useRecentStore((s) => s.items);
    const recentWorkspaces = useMemo(
        () =>
            recentItems
                .filter((item) => item.type === "folder" || item.type === "workspace")
                .slice(0, 8),
        [recentItems],
    );

    const allowedRootPaths = useMemo(
        () => allowedRoots.map((root) => root.path),
        [allowedRoots],
    );

    const refreshAllowedRoots = useCallback(() => {
        workspaceFs
            .allowedRoots()
            .then(setAllowedRoots)
            .catch(() => setAllowedRoots([]));
    }, []);

    // Load allowed roots once; refresh recents/favorites whenever currentPath changes
    useEffect(() => {
        refreshAllowedRoots();
        const bus = getNotificationBus();
        return bus.subscribe<{ changedKeys?: string[] }>(
            "event/config.changed",
            (params) => {
                if (
                    !params.changedKeys ||
                    params.changedKeys.some((key) => key.startsWith("engine.allowedOperationDirs"))
                ) {
                    refreshAllowedRoots();
                }
            },
        );
    }, [refreshAllowedRoots]);

    useEffect(() => {
        setRecents(
            getRecents().filter((entry) =>
                isPathWithinRoots(entry.path, allowedRootPaths),
            ),
        );
        setFavorites(
            getFavorites().filter((entry) =>
                isPathWithinRoots(entry.path, allowedRootPaths),
            ),
        );
    }, [allowedRootPaths, currentPath]);

    // Reset externalDragOver if the drag ends outside the window
    useEffect(() => {
        const onWindowDragEnd = () => setExternalDragOver(false);
        window.addEventListener("dragend", onWindowDragEnd);
        return () => window.removeEventListener("dragend", onWindowDragEnd);
    }, []);

    // Focus path input when shown
    useEffect(() => {
        if (pathInputVisible) {
            pathInputRef.current?.focus();
        }
    }, [pathInputVisible]);

    const handlePathSubmit = useCallback(() => {
        const p = normalizeUserPath(pathInput.trim());
        if (p) {
            if (!isPathWithinRoots(p, allowedRootPaths)) {
                setPathError(t("dialog.pathOutsideAllowedRoots"));
                return;
            }
            onNavigate(p);
            setPathInputVisible(false);
            setPathInput("");
            setPathError(null);
        }
    }, [allowedRootPaths, onNavigate, pathInput, t]);

    const handleAddFavorite = useCallback(() => {
        if (!isPathWithinRoots(currentPath, allowedRootPaths)) {
            return;
        }
        addFavorite(currentPath);
        setFavorites(
            getFavorites().filter((entry) =>
                isPathWithinRoots(entry.path, allowedRootPaths),
            ),
        );
    }, [allowedRootPaths, currentPath]);

    const handleRemoveFavorite = useCallback((path: string) => {
        removeFavorite(path);
        setFavorites(
            getFavorites().filter((entry) =>
                isPathWithinRoots(entry.path, allowedRootPaths),
            ),
        );
    }, [allowedRootPaths]);

    const handleClearRecents = useCallback(() => {
        clearRecents();
        setRecents([]);
    }, []);

    const handleRemoveRecent = useCallback((path: string) => {
        removeRecent(path);
        setRecents(
            getRecents().filter((entry) =>
                isPathWithinRoots(entry.path, allowedRootPaths),
            ),
        );
    }, [allowedRootPaths]);

    /* ── Drag-and-drop reorder for favorites ── */

    const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
        setDragIndex(index);
        e.dataTransfer.effectAllowed = "move";
        // Use a dedicated MIME to distinguish reorder from external drops
        e.dataTransfer.setData("application/x-magic-md-reorder", String(index));
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        // Only show reorder indicator for internal drag-reorder, not external drops
        if (dragIndex !== null) setDragOverIndex(index);
    }, [dragIndex]);

    const handleDrop = useCallback(
        (e: React.DragEvent, toIndex: number) => {
            e.preventDefault();
            // Only handle reorder drags (from within favorites)
            const reorderData = e.dataTransfer.getData("application/x-magic-md-reorder");
            if (!reorderData) return; // External drag — let it bubble to section handler
            const fromIndex = Number(reorderData);
            if (!Number.isNaN(fromIndex) && fromIndex !== toIndex) {
                const next = [...favorites];
                const [moved] = next.splice(fromIndex, 1);
                next.splice(toIndex, 0, moved);
                reorderFavorites(next);
                setFavorites(next);
            }
            setDragIndex(null);
            setDragOverIndex(null);
        },
        [favorites],
    );

    const handleDragEnd = useCallback(() => {
        setDragIndex(null);
        setDragOverIndex(null);
    }, []);

    /* ── External folder drop onto favorites section ── */

    const handleFavoritesDrop = useCallback(
        (e: React.DragEvent) => {
            // Ignore internal reorder drags
            if (e.dataTransfer.getData("application/x-magic-md-reorder")) return;

            e.preventDefault();
            // Accept path dropped from column view via custom MIME
            const droppedPath =
                e.dataTransfer.getData(DRAG_MIME_PATH) ||
                e.dataTransfer.getData("text/plain");
            if (
                droppedPath &&
                droppedPath.startsWith(sep) &&
                isPathWithinRoots(droppedPath, allowedRootPaths)
            ) {
                addFavorite(droppedPath);
                setFavorites(
                    getFavorites().filter((entry) =>
                        isPathWithinRoots(entry.path, allowedRootPaths),
                    ),
                );
            }
            setDragOverIndex(null);
        },
        [allowedRootPaths, sep],
    );

    const isActive = (path: string) => currentPath === path;

    // Recent paths to show as hints in path input (up to 3, excluding current)
    const recentHints = recents.filter((r) => r.path !== currentPath).slice(0, 3);

    return (
        <div className="flex h-full w-[220px] shrink-0 select-none flex-col border-r border-border bg-muted/10">
            {onOpenRecentWorkspace ? (
                <div
                    data-browser-sidebar-section="recent-workspaces"
                    className="shrink-0 border-b border-border/50 p-1.5"
                >
                    <SidebarSectionHeader
                        id="recent-workspaces"
                        icon={<Clock size={12} />}
                        title={t("dialog.recentWorkspaces")}
                        count={recentWorkspaces.length}
                        collapsed={recentWorkspacesCollapsed}
                        onToggle={() => setRecentWorkspacesCollapsed((v) => !v)}
                    />
                    {!recentWorkspacesCollapsed && (
                        recentWorkspaces.length === 0 ? (
                            <EmptySection>{t("dialog.noRecentWorkspaces")}</EmptySection>
                        ) : (
                            <div className="mt-0.5">
                                {recentWorkspaces.map((item) => (
                                    <SidebarItem
                                        key={`${item.type}:${item.path}`}
                                        icon={item.type === "workspace" ? <HardDrive size={13} /> : <Folder size={13} />}
                                        label={recentWorkspaceLabel(item)}
                                        active={isActive(item.path)}
                                        onClick={() => void onOpenRecentWorkspace(item)}
                                        title={item.path}
                                    />
                                ))}
                            </div>
                        )
                    )}
                </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
                {/* Favorites */}
                <div
                    data-browser-sidebar-section="favorites"
                    className={cn(
                        "border-b border-border/50 pb-1 mb-1 transition-colors",
                        externalDragOver && "bg-primary/5",
                    )}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "copy";
                        // Only show section highlight for external drags (not reorder)
                        if (dragIndex === null) setExternalDragOver(true);
                    }}
                    onDragLeave={(e) => {
                        // Only clear when leaving the section itself, not its children
                        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                            setExternalDragOver(false);
                        }
                    }}
                    onDrop={(e) => {
                        setExternalDragOver(false);
                        handleFavoritesDrop(e);
                    }}
                >
                    <SidebarSectionHeader
                        id="favorites"
                        icon={<Heart size={12} />}
                        title={t("dialog.favorites")}
                        count={favorites.length}
                        collapsed={favoritesCollapsed}
                        onToggle={() => setFavoritesCollapsed((v) => !v)}
                        actions={
                            <button
                                type="button"
                                onClick={handleAddFavorite}
                                title={t("dialog.addCurrentFolderToFavorites")}
                                className="p-0.5 rounded hover:bg-muted text-muted-foreground/50 hover:text-foreground transition-colors"
                            >
                                <Star size={10} />
                            </button>
                        }
                    />
                    {!favoritesCollapsed && (
                        favorites.length === 0 ? (
                            <div
                                className={cn(
                                    "mx-2 my-1 px-2 py-2 text-[10px] text-muted-foreground/40 italic rounded border border-dashed border-border/50 text-center transition-colors",
                                    externalDragOver && "border-primary/40 text-primary/60 bg-primary/5",
                                )}
                            >
                                {t("dialog.dragFoldersHere")}
                            </div>
                        ) : (
                            <>
                                {favorites.map((fav, i) => (
                                    <div
                                        key={fav.path}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, i)}
                                        onDragOver={(e) => handleDragOver(e, i)}
                                        onDrop={(e) => handleDrop(e, i)}
                                        onDragEnd={handleDragEnd}
                                        className={cn(
                                            dragOverIndex === i &&
                                            dragIndex !== null &&
                                            dragIndex !== i &&
                                            "border-t-2 border-primary",
                                        )}
                                    >
                                        <SidebarItem
                                            icon={
                                                <Heart
                                                    size={13}
                                                    className="text-primary/70"
                                                />
                                            }
                                            label={fav.name}
                                            active={isActive(fav.path)}
                                            onClick={() => onNavigate(fav.path)}
                                            onRemove={() => handleRemoveFavorite(fav.path)}
                                        />
                                    </div>
                                ))}
                                {/* Bottom drop zone — only visible during external drag */}
                                {externalDragOver && (
                                    <div className="mx-2 mt-1 px-2 py-1.5 rounded border border-dashed border-primary/40 text-[10px] text-primary/60 text-center bg-primary/5">
                                        {t("dialog.dropToAdd")}
                                    </div>
                                )}
                            </>
                        )
                    )}
                </div>

                {/* Recents */}
                <div
                    data-browser-sidebar-section="recents"
                    className="pb-1"
                >
                    <SidebarSectionHeader
                        id="recents"
                        icon={<Clock size={12} />}
                        title={t("dialog.recents")}
                        count={recents.length}
                        collapsed={recentsCollapsed}
                        onToggle={() => setRecentsCollapsed((v) => !v)}
                        actions={
                            recents.length > 0 ? (
                                <button
                                    type="button"
                                    onClick={handleClearRecents}
                                    title={t("dialog.clearRecents")}
                                    className="p-0.5 rounded hover:bg-muted text-muted-foreground/50 hover:text-foreground transition-colors"
                                >
                                    <Trash2 size={10} />
                                </button>
                            ) : null
                        }
                    />
                    {!recentsCollapsed && (
                        recents.length === 0 ? (
                            <EmptySection>{t("dialog.noRecentFolders")}</EmptySection>
                        ) : (
                            <div className="mt-0.5">
                                {recents.map((r) => (
                                    <SidebarItem
                                        key={r.path}
                                        icon={<Clock size={13} />}
                                        label={r.name}
                                        active={isActive(r.path)}
                                        onClick={() => onNavigate(r.path)}
                                        onRemove={() => handleRemoveRecent(r.path)}
                                        title={r.path}
                                    />
                                ))}
                            </div>
                        )
                    )}
                </div>
            </div>

            <div className="shrink-0 border-t border-border/50 p-1.5">
                <div data-browser-sidebar-section="allowed-roots" className="border-b border-border/50 pb-1 mb-1">
                    <SidebarSectionHeader
                        id="allowed-roots"
                        icon={<HardDrive size={12} />}
                        title={t("dialog.allowedRoots")}
                        count={allowedRoots.length}
                        collapsed={allowedRootsCollapsed}
                        onToggle={() => setAllowedRootsCollapsed((v) => !v)}
                    />
                    {!allowedRootsCollapsed && (
                        allowedRoots.length === 0 ? (
                            <EmptySection>{t("dialog.noAllowedRoots")}</EmptySection>
                        ) : (
                            <div className="mt-0.5 max-h-28 overflow-y-auto">
                                {allowedRoots.map((root) => (
                                    <SidebarItem
                                        key={`${root.source}:${root.path}`}
                                        icon={rootIcon(root)}
                                        label={root.label}
                                        active={isActive(root.path)}
                                        onClick={() => onNavigate(root.path)}
                                        title={root.path}
                                    />
                                ))}
                            </div>
                        )
                    )}
                </div>

                {/* Go-to path input */}
                {pathInputVisible ? (
                    <div>
                        <div className="flex items-center gap-1">
                            <input
                                ref={pathInputRef}
                                type="text"
                                value={pathInput}
                                onChange={(e) => {
                                    setPathInput(e.target.value);
                                    if (pathError) setPathError(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handlePathSubmit();
                                    if (e.key === "Escape") {
                                        setPathInputVisible(false);
                                        setPathInput("");
                                        setPathError(null);
                                    }
                                }}
                                onBlur={() => {
                                    if (!pathInput.trim()) {
                                        // Delay to allow hint clicks to register
                                        setTimeout(() => setPathInputVisible(false), 150);
                                    }
                                }}
                                placeholder={t("dialog.enterAllowedPath")}
                                className="flex-1 min-w-0 text-[11px] bg-muted/50 border border-border rounded px-1.5 py-1 outline-none focus:border-primary text-foreground placeholder:text-muted-foreground/40"
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setPathInputVisible(false);
                                    setPathInput("");
                                    setPathError(null);
                                }}
                                className="p-0.5 rounded hover:bg-muted text-muted-foreground"
                            >
                                <X size={10} />
                            </button>
                        </div>
                        {pathError ? (
                            <div className="mt-1 text-[10px] text-destructive">
                                {pathError}
                            </div>
                        ) : null}
                        {/* Recent path hints */}
                        {recentHints.length > 0 && !pathInput && (
                            <div className="mt-1 space-y-0.5">
                                {recentHints.map((r) => (
                                    <button
                                        key={r.path}
                                        type="button"
                                        onClick={() => {
                                            onNavigate(r.path);
                                            setPathInputVisible(false);
                                            setPathInput("");
                                        }}
                                        className="w-full text-left px-1.5 py-0.5 rounded text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-muted/60 truncate transition-colors"
                                        title={r.path}
                                    >
                                        {r.path}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => {
                            setPathInputVisible(true);
                            setPathError(null);
                        }}
                        className="w-full h-6 text-[11px] text-muted-foreground/70 hover:text-foreground hover:bg-muted/60 rounded px-2 text-left transition-colors"
                    >
                        {t("dialog.goToPath")}
                    </button>
                )}
            </div>
        </div>
    );
}

function SidebarSectionHeader({
    id,
    icon,
    title,
    count,
    collapsed,
    onToggle,
    actions,
}: {
    id: string;
    icon: React.ReactNode;
    title: string;
    count?: number;
    collapsed: boolean;
    onToggle: () => void;
    actions?: React.ReactNode;
}) {
    const label = count == null ? title : `${title} (${count})`;

    return (
        <div
            data-browser-sidebar-header={id}
            className="flex h-6 items-center justify-between gap-1"
        >
            <button
                type="button"
                aria-expanded={!collapsed}
                onClick={onToggle}
                className="flex h-6 min-w-0 flex-1 items-center rounded px-2 text-left text-xs transition-colors hover:bg-muted/60"
            >
                <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
                    {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                </span>
                <span className="mr-1.5 shrink-0 text-muted-foreground">{icon}</span>
                <span className="truncate text-[11px] leading-none text-muted-foreground uppercase tracking-wider">
                    {label}
                </span>
            </button>
            {actions ? (
                <div className="flex shrink-0 items-center pr-1">{actions}</div>
            ) : null}
        </div>
    );
}

function EmptySection({ children }: { children: React.ReactNode }) {
    return (
        <div className="px-3 py-2 text-[10px] italic text-muted-foreground/40">
            {children}
        </div>
    );
}

function recentWorkspaceLabel(item: RecentItem): string {
    const parts = item.path.split(/[/\\]/).filter(Boolean);
    const leaf = parts.at(-1) ?? item.name;
    const parent = parts.at(-2);
    if ((item.type === "workspace" || leaf.toLowerCase().endsWith(".mdspace")) && parent) {
        return `${parent}/${leaf}`;
    }
    return leaf;
}

function rootIcon(root: WorkspaceAllowedRoot): React.ReactNode {
    switch (root.source) {
        case "home":
            return <Home size={13} />;
        case "workspaceRoot":
            return <HardDrive size={13} />;
        default:
            return <Folder size={13} />;
    }
}

/* ── SidebarItem ── */

function SidebarItem({
    icon,
    label,
    active,
    onClick,
    onRemove,
    title,
    disabled,
}: {
    icon: React.ReactNode;
    label: string;
    active: boolean;
    onClick: () => void;
    onRemove?: () => void;
    title?: string;
    disabled?: boolean;
}) {
    return (
        <div className="group relative">
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                title={title ?? label}
                className={cn(
                    "w-full flex items-center gap-1.5 rounded px-2 py-1 text-xs text-left transition-colors truncate",
                    active
                        ? "bg-primary/10 text-primary font-medium"
                        : disabled
                            ? "text-muted-foreground/30 cursor-not-allowed"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                )}
            >
                <span className="shrink-0">{icon}</span>
                <span className="truncate">{label}</span>
            </button>
            {onRemove && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted text-muted-foreground/30 hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                >
                    <X size={10} />
                </button>
            )}
        </div>
    );
}
